import {api} from "@/shared/api";

import {PresenceBoardHelper} from "./presenceBoardHelper";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import {SecurityStatus} from "@/shared/securityStatus";
import {AbsentUser, HolidayUser, InactiveUser, NowActiveUser, NoWorkDayUser, PresenceBoardResponse} from "@/shared/timr";
import {PresenceBoardCategory, PresenceBoardFilterOptions, PresenceBoardVisibility} from "@/shared/timrTypes";

export class SidebarHelper {
	private static getPresenceBoardUsersRequest: JQuery.jqXHR<PresenceBoardResponse>;
	private static presenceBoardHelper: PresenceBoardHelper;

	public static async getPresenceBoardPopoverElement() {
		const deferred = $.Deferred<string>();

		this.getPresenceBoardUsersRequest?.abort();

		const showOtherUsers = BrowserStorageSettings.getPresenceBoardShowAllFilter() ?? true;
		const params = [];
		params.push({
			name: "presenceBoardCategoryFilter",
			value: BrowserStorageSettings.getPresenceBoardTypeFilter() || PresenceBoardFilterOptions.ALL
		});
		params.push({name: "showPresenceBoardOfOtherUsers", value: showOtherUsers});
		params.push({name: "groupId", value: showOtherUsers ? BrowserStorageSettings.getPresenceBoardGroupFilter()?.id : null});
		this.getPresenceBoardUsersRequest = api("reports/getPresenceBoardUsers.json")
			.post(params)
			.done((response: PresenceBoardResponse) => {
				SecurityStatus.update(response.securityStatus);
				PresenceBoardHelper.setPresenceBoardSessionSettings(response.presenceBoardVisibility);

				// If presenceboard got turned off while the user has the site open,
				// show an info message
				if (response.presenceBoardVisibility === PresenceBoardVisibility.OFF) {
					deferred.reject(
						$("<div>")
							.addClass("presence-board-hover-info")
							.append(
								$("<h3>")
									.addClass("flex-row flex-valign-middle text-muted")
									.append($("<i>").addClass("fal fa-info-circle mr-15"))
									.append($("<p>").addClass("mb-0").text(messages["presenceBoard.popover.invalid.settings"]))
							).prop("outerHTML") as string
					);
				}

				if (response.data?.length === 0) {
					deferred.resolve(
						$("<div>")
							.append(this.createPopoverTitle())
							.append(
								$("<div>")
									.addClass("presence-board-hover-info")
									.append(
										$("<h3>")
											.addClass("flex-row flex-valign-middle text-muted")
											.append($("<i>").addClass("fal fa-info-circle mr-15"))
											.append($("<p>").addClass("mb-0").text(messages["presenceBoard.users.notFound"]))
									)
							).prop("outerHTML") as string
					);
				}

				this.presenceBoardHelper = new PresenceBoardHelper({
					displayRunningProjectTime: response.showProjectTimeInPresenceBoard,
					reducedCards: true
				});

				const wrapper = $("<div>").append(this.createPopoverTitle());
				const cards = $("<div>").addClass("presence-board-view reduced");
				for (const presenceBoardUser of response.data) {
					switch (presenceBoardUser.presenceBoardUserType) {
						case PresenceBoardCategory.NOW_ACTIVE:
							cards.append(this.presenceBoardHelper.getActiveUserCard(presenceBoardUser as NowActiveUser));
							break;
						case PresenceBoardCategory.INACTIVE:
							cards.append(this.presenceBoardHelper.getInactiveUserCard(presenceBoardUser as InactiveUser));
							break;
						case PresenceBoardCategory.ABSENT:
							cards.append(this.presenceBoardHelper.getAbsentUserCard(presenceBoardUser as AbsentUser));
							break;
						case PresenceBoardCategory.HOLIDAY:
							cards.append(this.presenceBoardHelper.getHolidayUserCard(presenceBoardUser as HolidayUser));
							break;
						case PresenceBoardCategory.NO_WORK_DAY:
							cards.append(this.presenceBoardHelper.getNoWorkingDayUserCard(presenceBoardUser as NoWorkDayUser));
							break;
					}
				}
				wrapper.append(cards);
				deferred.resolve(wrapper.html());
			});
		return deferred.promise();
	}

	public static createLoadingCards(): string {
		if (!this.presenceBoardHelper) {
			this.presenceBoardHelper = new PresenceBoardHelper({});
		}

		// fill popover with loading cards
		const wrapper = $("<div>");
		wrapper.append(this.createPopoverTitle());
		const view = $("<div>").addClass("presence-board-view reduced");
		for (let i = 0; i < 6; i++) {
			const card = this.presenceBoardHelper.getReducedPresenceBoardUserCard({
				fullName: "\xa0",
				categoryDisplay: "",
				colorType: "inactive"
			});
			card.addClass("loading m-0");
			view.append(card);
		}
		wrapper.append(view);
		return wrapper.prop("outerHTML") as string;
	}

	private static createPopoverTitle() {
		let settingsStr: string;
		const presenceBoardShowOtherUsers = BrowserStorageSettings.getPresenceBoardShowAllFilter() ?? true;
		const presenceBoardCategoryFilter = BrowserStorageSettings.getPresenceBoardTypeFilter() ?? PresenceBoardFilterOptions.ALL;
		const groupFilter = presenceBoardShowOtherUsers ? BrowserStorageSettings.getPresenceBoardGroupFilter() : null;

		const filterMessage = messages[`presenceBoard.filter.${presenceBoardCategoryFilter.toLowerCase()}`];
		if (groupFilter?.name) {
			// "Organization <group name> <filter>"
			settingsStr = `${messages["presenceBoard.popover.group"]} ${groupFilter.name} ${filterMessage}`;
		} else if (!presenceBoardShowOtherUsers) {
			// "Teammates <filter>"
			settingsStr = `${messages["presenceBoard.view.group"]} ${filterMessage}`;
		} else if (presenceBoardShowOtherUsers && presenceBoardCategoryFilter === PresenceBoardFilterOptions.ALL) {
			// Show only "All" not "All All"
			settingsStr = `${filterMessage}`;
		} else if (presenceBoardShowOtherUsers) {
			// "All <filter>"
			settingsStr = `${messages["presenceBoard.view.all"]} ${filterMessage}`;
		}

		const title = $("<div>");
		title.append($("<h4>", {"title": settingsStr})
			.text(settingsStr)
			.addClass("ml-10 mt-10 mb-20 text-nowrap overflow-ellipsis"));
		return title;
	}
}
